import { useId } from 'react';

/**
 * **Un réglage à deux états, qui prend effet à l'instant où on le touche.**
 *
 * **Il n'y a pas de bouton « enregistrer » derrière**, et c'est ce qui le distingue d'une
 * case dans un formulaire : les trois préférences de `CDC-4` F-51 se posent une par une,
 * par une route qui rend l'état obtenu. Un formulaire à valider ferait attendre un geste
 * de plus pour un réglage qui n'a rien à confirmer.
 *
 * **C'est une CASE À COCHER, pas un `div` avec un `role`.** Elle est native, donc elle
 * arrive au clavier toute seule, elle s'annonce toute seule, et elle se coche à la barre
 * d'espace sans qu'on l'écrive. `design-system-first` le pose comme arbitrage général :
 * « **encapsuler le natif, ne pas le remplacer** — le minimalisme s'applique à l'intérieur
 * du composant, pas à la décision de créer le composant ».
 *
 * **L'apparence de rail est peinte SUR la case**, qui reste présente et cliquable en
 * dessous : `appearance-none` retire le dessin du navigateur, pas l'élément.
 */
type Props = {
  readonly libelle: string;
  readonly aide?: string;
  readonly actif: boolean;
  /** Vrai pendant que le serveur enregistre. Le réglage se bloque, il ne disparaît pas. */
  readonly enCours?: boolean;
  readonly onChanger: (actif: boolean) => void;
};

export function Interrupteur({
  libelle,
  aide,
  actif,
  enCours = false,
  onChanger,
}: Props) {
  const base = useId();
  const idAide = aide === undefined ? undefined : `${base}-aide`;

  return (
    <div className="flex items-start justify-between gap-4">
      <span className="flex flex-col gap-1">
        <label
          htmlFor={base}
          className="text-14 font-weight-700 text-texte-fort"
        >
          {libelle}
        </label>
        {aide !== undefined && (
          <p id={idAide} className="text-13 text-texte-faible">
            {aide}
          </p>
        )}
      </span>

      <span className="relative inline-flex shrink-0 items-center">
        <input
          id={base}
          type="checkbox"
          role="switch"
          checked={actif}
          disabled={enCours}
          aria-describedby={idAide}
          aria-busy={enCours || undefined}
          onChange={(evenement) => onChanger(evenement.target.checked)}
          className={[
            'peer h-6 w-11 cursor-pointer appearance-none rounded-plein',
            'border border-bord-champ bg-fond-bloc',
            'transition-colors duration-2',
            'checked:border-action checked:bg-action',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'focus-visible:outline focus-visible:outline-2',
            'focus-visible:outline-offset-2 focus-visible:outline-focus',
          ].join(' ')}
        />
        {/* La pastille qui glisse. `aria-hidden` : elle ne dit rien de plus que la case,
            qui porte déjà son état. */}
        <span
          aria-hidden="true"
          className={[
            'pointer-events-none absolute left-1 size-4 rounded-plein',
            'bg-fond-surface transition-transform duration-2',
            'peer-checked:translate-x-5',
          ].join(' ')}
        />
      </span>
    </div>
  );
}
